import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useIsTenantAdmin, useIsPlatformAdmin } from '../hooks/usePermission';
import ThemeToggle from '../components/ThemeToggle';
import useIdleLogout from '../hooks/useIdleLogout';
import TrialBanner from '../components/TrialBanner';
import OnboardingWizard from '../components/OnboardingWizard';
import PrivacyReacceptModal from '../components/PrivacyReacceptModal';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Scale,
  LayoutDashboard,
  FolderOpen,
  FileText,
  Clock,
  DollarSign,
  Shield,
  CheckSquare,
  Calendar,
  Users,
  Settings,
  LogOut,
  Menu,
  Building2,
  ChevronDown,
  History,
  Globe,
  UserCircle,
  UserCog,
  Crown,
  Sparkles,
  ScrollText,
  Link2,
  HelpCircle
} from 'lucide-react';

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  useIdleLogout();

  // Auto-open wizard on first visit for admins who haven't completed onboarding.
  // Regular staff invited via /auth/staff-invite skip this because the
  // inviter already set up the firm.
  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'platform_admin') return;
    if (user.onboarding_completed === true) return;
    // Small delay so the initial page render happens first
    const t = setTimeout(() => setOnboardingOpen(true), 600);
    return () => clearTimeout(t);
  }, [user]);
  const isAdmin = useIsTenantAdmin();
  const isPlatformAdmin = useIsPlatformAdmin();

  // Persist sidebar scroll across navigation so the cursor stays on the
  // selected item instead of jumping back to the top.
  const desktopNavRef = useRef(null);
  useLayoutEffect(() => {
    const el = desktopNavRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem('sidebar-scroll');
    if (saved) el.scrollTop = parseInt(saved, 10) || 0;
    const onScroll = () => {
      sessionStorage.setItem('sidebar-scroll', String(el.scrollTop));
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // When the route changes, keep the active item in view without jumping.
  useEffect(() => {
    const el = desktopNavRef.current;
    if (!el) return;
    const active = el.querySelector('.nav-link-active');
    if (active && typeof active.scrollIntoView === 'function') {
      const navRect = el.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();
      const outOfView = itemRect.top < navRect.top || itemRect.bottom > navRect.bottom;
      if (outOfView) {
        active.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [location.pathname]);

  // Ordered by daily usage frequency (most used → least used), grouped by role.
  // WORK (daily): dashboard → cases → calendar → tasks → time → clients
  // DELIVERY (weekly): documents → billing → trust → ai → templates
  // COLLAB & CONFIG (occasional): portal → integrations → help
  // ADMIN (rare): team → admin → audit → settings
  const navItems = [
    // Daily work
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/dashboard/cases', icon: FolderOpen, label: t('nav.cases') },
    { to: '/dashboard/calendar', icon: Calendar, label: t('nav.calendar') },
    { to: '/dashboard/tasks', icon: CheckSquare, label: t('nav.tasks') },
    { to: '/dashboard/time', icon: Clock, label: t('nav.timeTracking') },
    { to: '/dashboard/clients', icon: Users, label: t('nav.clients') },
    // Weekly delivery
    { to: '/dashboard/documents', icon: FileText, label: t('nav.documents') },
    { to: '/dashboard/billing', icon: DollarSign, label: t('nav.billing') },
    { to: '/dashboard/trust', icon: Shield, label: t('nav.trust') },
    { to: '/dashboard/ai', icon: Sparkles, label: t('nav.aiAssistant') },
    { to: '/dashboard/templates', icon: ScrollText, label: t('nav.templates') },
    // Occasional
    { to: '/dashboard/portal', icon: UserCircle, label: t('nav.clientPortal') },
    { to: '/dashboard/integrations', icon: Link2, label: t('nav.integrations') },
    { to: '/dashboard/help', icon: HelpCircle, label: t('nav.help') },
    // Admin / compliance
    { to: '/dashboard/affiliates', icon: Link2, label: t('nav.affiliates'), affiliateOnly: true },
    { to: '/dashboard/team', icon: UserCog, label: t('nav.team'), adminOnly: true },
    { to: '/dashboard/admin', icon: Crown, label: t('nav.adminPortal'), platformAdminOnly: true },
    { to: '/dashboard/audit', icon: History, label: t('nav.auditLog') },
    { to: '/dashboard/settings', icon: Settings, label: t('nav.settings') },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  function SidebarNav({ onNavClick, navRef }) {
    const visibleItems = navItems.filter((item) => {
      if (item.adminOnly && !isAdmin) return false;
      if (item.platformAdminOnly && !isPlatformAdmin) return false;
      if (item.affiliateOnly && !user?.affiliate_active) return false;
      return true;
    });
    return (
      <nav ref={navRef} className="flex-1 p-4 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.to ||
            (item.to !== '/dashboard' && location.pathname.startsWith(item.to));

          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavClick}
              data-testid={`nav-${item.to.split('/').pop()}`}
              className={`nav-link ${isActive ? 'nav-link-active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
      {/* Skip-to-content link: visible only when focused via keyboard so
          power users (and screen readers) can bypass the sidebar. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-navy focus:text-white focus:rounded"
      >
        {language === 'es' ? 'Saltar al contenido' : 'Skip to content'}
      </a>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 bg-navy flex-col fixed inset-y-0 left-0 z-40" aria-label={language === 'es' ? 'Navegación principal' : 'Main navigation'}>
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
          <div className="w-8 h-8 bg-white/10 rounded-sm flex items-center justify-center">
            <Scale className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-serif font-bold text-white">ERP Legal</span>
        </div>

        {/* Tenant Info */}
        {user?.tenant_id && (
          <div className="px-4 py-4 border-b border-white/10">
            <div className="flex items-center gap-3 px-2">
              <Building2 className="w-4 h-4 text-white/60" />
              <span className="text-sm text-white/80 truncate">{language === 'en' ? 'My Law Firm' : 'Mi Bufete'}</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <SidebarNav navRef={desktopNavRef} />

        {/* Language + Theme Toggles */}
        <div className="px-4 py-2 border-t border-white/10 flex items-center gap-2">
          <button
            onClick={toggleLanguage}
            className="flex-1 flex items-center gap-3 px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
            data-testid="sidebar-language-toggle"
          >
            <Globe className="w-5 h-5" />
            <span>{language === 'en' ? 'Español' : 'English'}</span>
          </button>
          <ThemeToggle variant="dark" />
        </div>

        {/* User Menu */}
        <div className="p-4 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-white/10 transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-bronze text-white text-sm">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  <p className="text-xs text-white/60 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-white/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="w-4 h-4 mr-2" />
                {t('nav.settings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-background border-b border-border z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-navy rounded-sm flex items-center justify-center">
            <Scale className="w-4 h-4 text-white" />
          </div>
          <span className="text-lg font-serif font-bold text-navy">ERP Legal</span>
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="gap-1"
            aria-label={language === 'en' ? 'Change language to Spanish' : 'Cambiar idioma a Inglés'}
          >
            <Globe className="w-4 h-4" aria-hidden="true" />
            {language === 'en' ? 'ES' : 'EN'}
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"
                aria-label={language === 'es' ? 'Abrir menú de navegación' : 'Open navigation menu'}>
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-navy border-none">
              <div className="h-16 flex items-center gap-3 px-6 border-b border-white/10">
                <div className="w-8 h-8 bg-white/10 rounded-sm flex items-center justify-center">
                  <Scale className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-serif font-bold text-white">ERP Legal</span>
              </div>
              <SidebarNav onNavClick={() => setMobileOpen(false)} />
              <div className="p-4 border-t border-white/10">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>{t('nav.signOut')}</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="flex-1 lg:ml-64 min-h-screen" tabIndex="-1">
        <div className="pt-16 lg:pt-0 px-4 lg:px-0 pt-4">
          <TrialBanner />
        </div>
        <div>
          <Outlet />
        </div>
      </main>

      <OnboardingWizard open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
      <PrivacyReacceptModal />
    </div>
  );
}
